import type { CancelTokenSource } from 'axios';
import {
  useCallback,
  useMemo, useRef, useState,
} from 'react';
import { createCancelToken } from '~/_shared/utils/api/api.helpers';
import {
  getBulkSpreadsheetDataBroadcast, SpreadsheetDataExportEventStatus,
} from '~/_shared/utils/api/broadcast.helpers';
import { useIsComponentMountedRef } from '~/_shared/utils/hooks/useIsComponentMountedRef';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { useIsMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';
import {
  getSpreadsheetBulkDataAsync,
  getSpreadsheetBulkDataAsyncResults, type SpreadsheetDataBulkRequest,
  type SpreadsheetDataBulkResponse,
  SpreadsheetDataBulkStatus,
} from './spreadsheet.repository';

export const useGetBulkSpreadsheetDataAsynchronously = () => {
  const mapId = useMapIdSelector();
  const clientId = useClientIdSelector();
  const isMountedRef = useIsComponentMountedRef();
  const isPresentational = useIsMapPresentationalSelector();

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const cancelToken = useRef<CancelTokenSource | null>(null);

  const setLoadingWhenMounted = useCallback((newIsLoading: boolean) => {
    if (isMountedRef.current) {
      setIsLoading(newIsLoading);
    }
  }, [isMountedRef]);

  const getSpreadsheetData = useCallback((request: SpreadsheetDataBulkRequest) => {
    if (!mapId || !clientId) {
      return Promise.reject();
    }

    cancelToken.current?.cancel();
    cancelToken.current = createCancelToken();

    setIsLoading(true);
    setIsError(false);

    return new Promise<SpreadsheetDataBulkResponse[]>((resolve, reject) => {
      const onReject = (error?: string) => {
        setLoadingWhenMounted(false);
        reject(error);
      };

      const onResultsReady = (hash: string) => {
        getSpreadsheetBulkDataAsyncResults(clientId, hash, isPresentational ? mapId : undefined)
          .then(results => {
            if (results.data) {
              resolve(results.data);
            }
            else {
              onReject();
            }
          })
          .finally(() => {
            setLoadingWhenMounted(false);
          });
      };

      const checkIfResultsReadyOrTryAgain = (hash: string, attemptNumber: number) => {
        getSpreadsheetBulkDataAsyncResults(clientId, hash, isPresentational ? mapId : undefined)
          .then(results => {
            if (results.message && [SpreadsheetDataBulkStatus.Processing, SpreadsheetDataBulkStatus.Initialized].includes(results.message) && attemptNumber < 25) {
              setTimeout(() => checkIfResultsReadyOrTryAgain(hash, attemptNumber++), 5000);
            }
            else if (!results.message && results.data) {
              resolve(results.data);
              setLoadingWhenMounted(false);
            }
            else {
              onReject();
              setLoadingWhenMounted(false);
            }
          });
      };

      getSpreadsheetBulkDataAsync(clientId, request, cancelToken.current?.token)
        .then(response => {
          if ([SpreadsheetDataBulkStatus.Initialized, SpreadsheetDataBulkStatus.Processing].includes(response.message)) {
            getBulkSpreadsheetDataBroadcast(e => { // if user is subscribed to WS broadcast let's use it
              if (e.hash === response.hash) {
                if (e.status === SpreadsheetDataExportEventStatus.Success) {
                  onResultsReady(response.hash);
                }
                else if (e.status === SpreadsheetDataExportEventStatus.Failed) {
                  onReject();
                }
              }
            }, () => { // if user is not subscribed to WS broadcast, we will attempt to get results every 5 seconds
              checkIfResultsReadyOrTryAgain(response.hash, 0);
            });
          }
          else {
            onReject();
          }
        })
        .catch(error => {
          onReject(error);
        });
    });
  }, [clientId, isPresentational, mapId, setLoadingWhenMounted]);

  return useMemo(() => ({
    isLoading,
    isError,
    getSpreadsheetData,
  }), [getSpreadsheetData, isError, isLoading]);
};
